import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getMyVenueRequests, updateRequestStatus, updateInterestStatus } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SendInviteModal from '../components/SendInviteModal';
import { TravelStatusBadge } from '../components/TravelConfirmationModal';
import {
  MapPin,
  Calendar,
  Clock,
  Euro,
  Users,
  Plus,
  Eye,
  XCircle,
  CheckCircle2,
  MessageCircle,
  Pencil,
  UserX,
  Send,
  Lock,
} from 'lucide-react';
import {
  cn,
  formatDate,
  formatTime,
  getUrgencyBadgeClass,
  getUrgencyBorderClass,
} from '../lib/utils';
import { DateWithRelative } from '../components/DateWithRelative';
import { toast } from 'sonner';
import { PageSkeleton, Bone, BoneCard } from '../components/PageSkeleton';

export default function ManageRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRequest, setExpandedRequest] = useState(null);
  
  // Send Invite Modal state
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedInterest, setSelectedInterest] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const response = await getMyVenueRequests();
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      await updateRequestStatus(requestId, newStatus);
      toast.success('Status updated');
      await loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const getUrgencyLabel = (urgency) => {
    switch (urgency) {
      case 'CRITICAL':
        return 'Critical';
      case 'TODAY':
        return 'Today';
      case 'THIS_WEEK':
        return 'This Week';
      default:
        return 'Flexible';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'OPEN':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/50">Open</Badge>;
      case 'FILLED':
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/50">Filled</Badge>;
      case 'CANCELLED':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/50">Cancelled</Badge>;
      case 'EXPIRED':
        return <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/50">Expired</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="content-container">
        <PageSkeleton label="Loading your requests">
          <div className="flex items-start justify-between gap-4">
            <div><Bone w={90} h={30} tone="card" /><Bone w={140} h={30} tone="card" className="mt-2" /></div>
            <Bone w={145} h={40} r="md" tone="amber" className="mt-6" />
          </div>
          <Bone w={90} h={14} className="mt-4" />
          <div className="space-y-4 mt-6">
            {[1, 2, 3].map((i) => (
              <BoneCard key={i} className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex gap-2"><Bone w={48} h={22} r="pill" /><Bone w={76} h={22} r="pill" /><Bone w={68} h={22} r="pill" /></div>
                  <Bone w={56} h={20} tone="amber" />
                </div>
                <Bone w="72%" h={20} className="mt-4" />
                <Bone w="44%" h={20} className="mt-2" />
                <Bone w="78%" h={14} className="mt-4" />
                <Bone w="56%" h={14} className="mt-2" />
                <div className="flex flex-wrap gap-2 mt-4"><Bone w={78} h={32} r="sm" /><Bone w={72} h={32} r="sm" /><Bone w={110} h={32} r="sm" /></div>
              </BoneCard>
            ))}
          </div>
        </PageSkeleton>
      </div>
    );
  }

  return (
    <div className="content-container">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="page-title gold-accent">My Requests</h1>
          <p className="text-muted-foreground mt-2">
            {requests.length} request{requests.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Link to="/venues/requests/new">
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="new-request-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Request
          </Button>
        </Link>
      </div>

      {requests.length === 0 ? (
        <Card className="p-8 text-center bg-card border-border">
          <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">You haven't created any requests yet</p>
          <Link to="/venues/requests/new">
            <Button className="bg-primary text-primary-foreground">Create Your First Request</Button>
          </Link>
        </Card>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <Card
              key={request.id}
              className={cn('card-base overflow-hidden', getUrgencyBorderClass(request.urgency))}
            >
              <CardContent className="p-4 md:p-6">
                <div className="flex flex-col gap-4">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        {getStatusBadge(request.status)}
                        <Badge className={getUrgencyBadgeClass(request.urgency)}>
                          {getUrgencyLabel(request.urgency)}
                        </Badge>
                        <Badge variant="outline" className="border-border bg-secondary/50">
                          {request.roleTypeNeeded === 'MUSICIAN' ? 'Musician' : 'Tech'}
                        </Badge>
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">{request.title}</h3>
                    </div>
                    {request.budgetAmount && (
                      <div className="text-right shrink-0">
                        <div className="flex items-center gap-1 text-lg font-semibold text-primary">
                          <Euro className="h-4 w-4" />
                          {request.budgetAmount}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {request.city}
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <DateWithRelative date={request.eventDate} />
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {formatTime(request.startTime)} - {formatTime(request.endTime)}
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {request.interests?.length || 0} interested
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    <Link to={`/requests/${request.reference || request.id}`}>
                      <Button variant="outline" size="sm" className="border-border bg-secondary/50 hover:bg-secondary" data-testid={`view-request-${request.id}`}>
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </Link>
                    {request.status === 'OPEN' && (
                      <>
                        <Link to={`/venues/requests/${request.id}/edit`}>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-primary/50 text-primary hover:bg-primary/20"
                            data-testid={`edit-request-${request.id}`}
                          >
                            <Pencil className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                        </Link>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-green-500/50 text-green-400 hover:bg-green-500/20"
                          onClick={() => handleStatusChange(request.id, 'FILLED')}
                          data-testid={`fill-request-${request.id}`}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          Mark Filled
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                          onClick={() => handleStatusChange(request.id, 'CANCELLED')}
                          data-testid={`cancel-request-${request.id}`}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                      </>
                    )}
                    {request.interests?.length > 0 && (
                      <Button
                        variant="secondary"
                        size="sm"
                        className="bg-secondary border-border"
                        onClick={() =>
                          setExpandedRequest(expandedRequest === request.id ? null : request.id)
                        }
                        data-testid={`toggle-interests-${request.id}`}
                      >
                        <Users className="h-4 w-4 mr-1" />
                        {expandedRequest === request.id ? 'Hide' : 'Show'} Interested (
                        {request.interests.length})
                      </Button>
                    )}
                  </div>

                  {/* Interested Profiles */}
                  {expandedRequest === request.id && request.interests?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-border space-y-3">
                      <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                        Interested Profiles
                      </h4>
                      {request.interests.map((interest) => {
                        const profile = interest.profile;
                        if (!profile) return null;

                        const interestStatus = interest.status || 'pending';
                        const inviteStatus = interest.inviteStatus;
                        const bookingConfirmed = interest.bookingConfirmed;

                        const handleSendInvite = () => {
                          setSelectedRequest(request);
                          setSelectedInterest(interest);
                          setSelectedProfile(profile);
                          setInviteModalOpen(true);
                        };

                        const handleReject = async () => {
                          try {
                            await updateInterestStatus(request.id, interest.id, 'rejected');
                            toast.success(`Rejected ${profile.displayName}`);
                            loadRequests();
                          } catch (error) {
                            toast.error('Failed to reject');
                          }
                        };

                        return (
                          <div
                            key={interest.id}
                            className={cn(
                              "p-3 rounded-lg border",
                              (bookingConfirmed || inviteStatus === 'CONFIRMED') && "bg-green-500/10 border-green-500/30",
                              inviteStatus === 'PENDING' && "bg-yellow-500/10 border-yellow-500/30",
                              inviteStatus === 'DECLINED' && "bg-red-500/10 border-red-500/30",
                              inviteStatus === 'EXPIRED' && "bg-zinc-500/10 border-zinc-500/30",
                              interestStatus === 'rejected' && "bg-red-500/10 border-red-500/30",
                              !inviteStatus && interestStatus === 'pending' && "bg-secondary/30 border-border"
                            )}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Link
                                    to={`/${profile.type === 'MUSICIAN' ? 'musicians' : 'tech'}/${profile.id}`}
                                    className="font-medium text-primary hover:underline truncate"
                                  >
                                    {profile.displayName}
                                  </Link>
                                  {bookingConfirmed && (
                                    <Badge className="bg-green-500/20 text-green-400 border-green-500/50 text-xs">
                                      Confirmed
                                    </Badge>
                                  )}
                                  {inviteStatus === 'PENDING' && (
                                    <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50 text-xs">
                                      Invite Sent
                                    </Badge>
                                  )}
                                  {inviteStatus === 'CONFIRMED' && !bookingConfirmed && (
                                    <Badge className="bg-green-500/20 text-green-400 border-green-500/50 text-xs">
                                      Invite Confirmed
                                    </Badge>
                                  )}
                                  {inviteStatus === 'DECLINED' && (
                                    <Badge className="bg-red-500/20 text-red-400 border-red-500/50 text-xs">
                                      Declined
                                    </Badge>
                                  )}
                                  {inviteStatus === 'EXPIRED' && (
                                    <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/50 text-xs">
                                      Expired
                                    </Badge>
                                  )}
                                  {inviteStatus === 'CANCELLED' && (
                                    <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/50 text-xs">
                                      Cancelled
                                    </Badge>
                                  )}
                                  {interestStatus === 'rejected' && (
                                    <Badge className="bg-red-500/20 text-red-400 border-red-500/50 text-xs">Rejected</Badge>
                                  )}
                                </div>
                                {/* Travel Status */}
                                <div className="mt-1">
                                  <TravelStatusBadge interest={interest} />
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {profile.city}
                                  {profile.baseRate && (
                                    <span className="ml-2">• <span className="text-primary">€{profile.baseRate}</span></span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                {/* No invite yet — can send one */}
                                {!inviteStatus && interestStatus !== 'rejected' && (
                                  <>
                                    <Button
                                      size="sm"
                                      className="h-8 bg-primary/20 text-primary hover:bg-primary/30"
                                      onClick={handleSendInvite}
                                      title="Send Invite"
                                      data-testid={`send-invite-${interest.id}`}
                                    >
                                      <Send className="h-4 w-4 mr-1" />
                                      Invite
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 w-8 p-0 text-red-400 hover:bg-red-500/20"
                                      onClick={handleReject}
                                      title="Reject"
                                      data-testid={`reject-${interest.id}`}
                                    >
                                      <UserX className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                                {/* Invite pending — awaiting talent response */}
                                {inviteStatus === 'PENDING' && (
                                  <span className="text-xs text-yellow-400 italic">Awaiting response…</span>
                                )}
                                {/* Confirmed — show WhatsApp button */}
                                {(inviteStatus === 'CONFIRMED' || bookingConfirmed) && interest.bookingId && (
                                  <Link to={`/bookings/${interest.bookingId}`}>
                                    <Button
                                      size="sm"
                                      className="h-8 bg-[#25D366] hover:bg-[#128C7E] text-white"
                                      data-testid={`whatsapp-${profile.id}`}
                                    >
                                      <MessageCircle className="h-4 w-4 mr-1" />
                                      WhatsApp
                                    </Button>
                                  </Link>
                                )}
                                {/* Declined / expired / cancelled — can re-invite */}
                                {(inviteStatus === 'DECLINED' || inviteStatus === 'EXPIRED' || inviteStatus === 'CANCELLED') && (
                                  <Button
                                    size="sm"
                                    className="h-8 bg-primary/20 text-primary hover:bg-primary/30"
                                    onClick={handleSendInvite}
                                    title="Send New Invite"
                                    data-testid={`reinvite-${interest.id}`}
                                  >
                                    <Send className="h-4 w-4 mr-1" />
                                    Re-invite
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Send Invite Modal (v2) */}
      <SendInviteModal
        open={inviteModalOpen}
        onOpenChange={setInviteModalOpen}
        request={selectedRequest}
        interest={selectedInterest}
        profile={selectedProfile}
        onSuccess={loadRequests}
      />
    </div>
  );
}
